// LayoutPageViewTypeModal.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { buildPageLayoutPickerTypeKeys } from 'pages/layout/layoutPageUtils';
import { getNodeTypePluralLabelFromDefinition } from 'utilities/NodeTypeLabels';

// CSS
import './LayoutPageViewTypeModal.css';

// Components
import Modal from 'components/modal/Modal';
import AddViewCardModalOption from 'dashboard/modals/option/AddViewCardModalOption';

/**
 * LayoutPageViewTypeModal
 *
 * Lets the author choose the related type for a new page-local view block.
 *
 * @param {string} [hostTypeKey=''] - Layout host type key to exclude from the picker.
 * @param {boolean} isOpen - Whether the picker modal is open.
 * @param {function} onClose - Closes the modal.
 * @param {function} onSelect - Called with the selected related type key.
 * @param {string} [title='Add a list'] - Modal title.
 * @param {function|null} [getAllTypeDefs] - Optional type catalog override for non-base editors.
 * @param {string} [viewNoun='list'] - Lowercase view label used in helper copy.
 *
 * @returns {ReactNode} The rendered related-type picker modal.
 */
const LayoutPageViewTypeModal = ({
	getAllTypeDefs = null,
	hostTypeKey = '',
	isOpen,
	onClose,
	onSelect,
	title = 'Add a list',
	viewNoun = 'list',
}) => {

	// ----- CONTEXT ----- //

	const { getAll: getAllFromContext } = useNodeTypes();
	const resolvedGetAllTypeDefs = typeof getAllTypeDefs === 'function'
		? getAllTypeDefs
		: getAllFromContext;
	// ----- DEVICE DETECTION ----- //

	const isMobile = useIsMobile();

	// ----- STATE ----- //

	const [selectingKey, setSelectingKey] = useState('');

	// ----- MEMOS ----- //

	const typeOptions = useMemo(() => {
		const typeDefs = Array.isArray(resolvedGetAllTypeDefs?.())
			? resolvedGetAllTypeDefs()
			: [];
		const typeDefByKey = new Map();

		typeDefs.forEach((typeDef) => {
			const typeKey = String(typeDef?.key || '').trim();
			if (typeKey) {
				typeDefByKey.set(typeKey, typeDef);
			}
		});
		return buildPageLayoutPickerTypeKeys({
			getAllTypeDefs: resolvedGetAllTypeDefs,
			hostTypeKey,
		})
			.map((typeKey) => {
				const typeDef = typeDefByKey.get(typeKey) || null;
				if (!typeDef) return null;

				const pluralLabel = getNodeTypePluralLabelFromDefinition(typeDef, typeKey, { fallback: 'Items' });
				const description = String(typeDef?.description || '').trim();
				const iconKey = String(typeDef?.iconKey || '').trim() || 'deployed_code';

				return {
					iconKey,
					key: typeKey,
					note: description || `Create a ${viewNoun} for ${pluralLabel}.`,
					title: pluralLabel,
				};
			})
			.filter(Boolean)
			.sort((left, right) => left.title.localeCompare(right.title));
	}, [hostTypeKey, resolvedGetAllTypeDefs, viewNoun]);

	/**
	 * handleSelect
	 *
	 * Handles a selection change.
	 *
	 * @param {string} typeKey - typeKey.
	 * @returns {Promise<void>} Resolves when the handler completes.
	 */
	async function handleSelect(typeKey) {
		const cleanedTypeKey = String(typeKey || '').trim();
		if (!cleanedTypeKey) return;

		setSelectingKey(cleanedTypeKey);

		try {
			await onSelect?.(cleanedTypeKey);
		} finally {
			setSelectingKey('');
		}
	}

	// ----- RENDER ----- //

	return (
		<Modal
			height="auto"
			isOpen={isOpen}
			title={title}
			width={isMobile ? '100%' : 520}
			onClose={onClose}
		>
			<div className="layout-page-view-type-modal-container">

				{/* SUBTITLE */}
				<div
					className="layout-page-view-type-modal-subtitle"
				>
					Choose which type this {viewNoun} should show. Every base type is available so you can create placeholder views for relationships you plan to add later.
				</div>

				{typeOptions.length ? (

					<div className="layout-page-view-type-modal-list">
						{typeOptions.map((option) => (
							<div
								className="layout-page-view-type-modal-row"
								style={{
									opacity: selectingKey === option.key ? 0.6 : 1,
								}}
								key={option.key}
							>
								<AddViewCardModalOption
									iconKey={option.iconKey}
									note={option.note}
									title={option.title}
									onClick={() => handleSelect(option.key)}
								/>
							</div>
						))}
					</div>
				) : (
					<div
						className="layout-page-view-type-modal-empty"
					>
						No base types are available yet.
					</div>
				)}
			</div>
		</Modal>
	);
};

// Dev Tools
LayoutPageViewTypeModal.displayName = 'LayoutPageViewTypeModal';

export default LayoutPageViewTypeModal;
